import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Eye, CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ReportData } from '@/types/report';
import {
  aggregatePendingValues,
  formatReportFieldValue,
  labelOfReportField,
  type ReportChangeLog,
} from '@/utils/reportEditUtils';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// ---------- page ----------

interface PendingGroup {
  report: ReportData;
  pendings: ReportChangeLog[];
  aggregated: Record<string, unknown>;
}

const AdminEditRequestsPage: React.FC = () => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [acting, setActing] = useState<Record<string, boolean>>({});
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);

  // 1) Load all PENDING TEACHER_REQUEST
  const {
    data: pendings = [],
    isLoading: pendingsLoading,
    refetch: refetchPendings,
  } = useQuery({
    queryKey: ['admin-pending-edits'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('report_change_logs')
        .select('*')
        .eq('status', 'PENDING')
        .eq('type', 'TEACHER_REQUEST')
        .order('created_at', { ascending: true });
      if (error) {
        console.warn('Could not load pending requests:', error.message);
        return [] as ReportChangeLog[];
      }
      return (data ?? []) as ReportChangeLog[];
    },
  });

  // 2) Load reports สำหรับ report_id ที่มี PENDING
  const reportIds = useMemo(
    () => Array.from(new Set(pendings.map((p) => p.report_id))),
    [pendings]
  );

  const { data: reports = [], isLoading: reportsLoading, refetch: refetchReports } =
    useQuery({
      queryKey: ['admin-pending-reports', reportIds.join(',')],
      queryFn: async () => {
        if (reportIds.length === 0) return [] as ReportData[];
        const { data, error } = await supabase
          .from('student_development_reports')
          .select('*')
          .in('id', reportIds);
        if (error) throw error;
        return (data ?? []) as ReportData[];
      },
      enabled: reportIds.length > 0,
    });

  // 3) Group + aggregate
  const groups: PendingGroup[] = useMemo(() => {
    return reportIds
      .map((rid) => {
        const report = reports.find((r) => r.id === rid);
        if (!report) return null;
        const rps = pendings.filter((p) => p.report_id === rid);
        const aggregated = aggregatePendingValues(rps);
        return { report, pendings: rps, aggregated };
      })
      .filter((g): g is PendingGroup => g !== null)
      .sort((a, b) => {
        // เรียงตาม PENDING ตัวเก่าสุดของกลุ่ม (มาก่อน-มาก่อน)
        const aOldest = Math.min(...a.pendings.map((p) => new Date(p.created_at).getTime()));
        const bOldest = Math.min(...b.pendings.map((p) => new Date(p.created_at).getTime()));
        return aOldest - bOldest;
      });
  }, [reportIds, reports, pendings]);

  const toggleExpanded = (reportId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  const refetchAll = async () => {
    await refetchPendings();
    await refetchReports();
  };

  const handleApprove = async (reportId: string) => {
    setActing((s) => ({ ...s, [reportId]: true }));
    try {
      const { error } = await (supabase as any).rpc('approve_edit_requests', {
        p_report_id: reportId,
      });
      if (error) {
        console.error('approve_edit_requests error:', error);
        toast({
          title: 'อนุมัติไม่สำเร็จ',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'อนุมัติสำเร็จ',
        description: 'อัปเดต report และทำเครื่องหมาย log เป็น APPROVED แล้ว',
      });
      await refetchAll();
    } finally {
      setActing((s) => ({ ...s, [reportId]: false }));
    }
  };

  const handleReject = async (reportId: string) => {
    setActing((s) => ({ ...s, [reportId]: true }));
    try {
      const { error } = await (supabase as any).rpc('reject_edit_requests', {
        p_report_id: reportId,
      });
      if (error) {
        console.error('reject_edit_requests error:', error);
        toast({
          title: 'ปฏิเสธไม่สำเร็จ',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'ปฏิเสธคำขอเรียบร้อย',
        description: 'log ทั้งหมดของ report นี้กลายเป็น REJECTED',
      });
      await refetchAll();
    } finally {
      setActing((s) => ({ ...s, [reportId]: false }));
      setRejectTargetId(null);
    }
  };

  const isLoading = pendingsLoading || reportsLoading;

  return (
    <div className="container max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">คำขอแก้ไขรายงาน</h1>
          <p className="text-sm text-gray-500 mt-1">
            รวม PENDING TEACHER_REQUEST จัดกลุ่มตาม report — approve/reject ทำเป็นชุด
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          {pendings.length} รายการรอ
        </Badge>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-gray-500">กำลังโหลด...</CardContent>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-gray-500">
            ไม่มีคำขอแก้ไขที่รอ approve
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(({ report, pendings: rps, aggregated }) => {
            const isExpanded = expanded.has(report.id);
            const isActing = !!acting[report.id];
            const changedFields = Object.keys(aggregated);
            return (
              <Card key={report.id} className="border-amber-200">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {report.student_nickname}{' '}
                        <span className="text-gray-500 text-sm font-normal">
                          ({report.student_full_name})
                        </span>
                      </CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        วันที่สอน: <strong>{report.teaching_date}</strong> ·
                        ครู: <strong>{report.teacher_display || report.teacher_id}</strong> ·
                        วิชา: <strong>{report.subject}</strong>
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          PENDING {rps.length} รายการ
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          แก้ {changedFields.length} ฟิลด์
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/reports/${report.id}`}>
                          <Eye className="h-4 w-4 mr-1" />
                          ดู report
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleExpanded(report.id)}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            ซ่อน
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            ดูรายละเอียด
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="space-y-4 pt-0">
                    {/* Diff table */}
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-1/4">ฟิลด์</TableHead>
                            <TableHead className="w-3/8">ค่าปัจจุบัน</TableHead>
                            <TableHead className="w-3/8">ค่าที่จะ apply (latest)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {changedFields.map((field) => (
                            <TableRow key={field}>
                              <TableCell className="font-medium text-sm">
                                {labelOfReportField(field)}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600 whitespace-pre-wrap">
                                {formatReportFieldValue((report as Record<string, unknown>)[field])}
                              </TableCell>
                              <TableCell className="text-sm font-medium whitespace-pre-wrap">
                                {formatReportFieldValue(aggregated[field])}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* List of underlying PENDING rows + reasons */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700">
                        คำขอย่อย ({rps.length})
                      </h4>
                      {rps.map((p, idx) => (
                        <div
                          key={p.id}
                          className="rounded border bg-gray-50 p-3 text-xs space-y-1"
                        >
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            <span>
                              <strong>#{idx + 1}</strong>
                            </span>
                            <span>{format(new Date(p.created_at), 'dd/MM/yyyy HH:mm')}</span>
                            <span>โดย: {p.created_by}</span>
                            <span>
                              ฟิลด์: <strong>{Object.keys(p.after).join(', ') || '—'}</strong>
                            </span>
                          </div>
                          {p.reason && (
                            <div className="text-gray-700">
                              <span className="text-gray-500">เหตุผล:</span> {p.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}

                <CardContent className="pt-0 flex flex-wrap gap-2 justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isActing}
                    onClick={() => setRejectTargetId(report.id)}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    ปฏิเสธทั้งหมด
                  </Button>
                  <Button
                    size="sm"
                    disabled={isActing}
                    onClick={() => handleApprove(report.id)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    {isActing ? 'กำลังดำเนินการ...' : 'อนุมัติทั้งหมด'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Reject confirm dialog */}
      <AlertDialog
        open={rejectTargetId !== null}
        onOpenChange={(open) => {
          if (!open) setRejectTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการปฏิเสธ</AlertDialogTitle>
            <AlertDialogDescription>
              คำขอ PENDING ทั้งหมดของ report นี้จะกลายเป็น REJECTED — ครูจะต้องส่งคำขอใหม่ถ้าต้องการแก้
              ดำเนินการต่อ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (rejectTargetId) handleReject(rejectTargetId);
              }}
            >
              ปฏิเสธทั้งหมด
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminEditRequestsPage;
